import { computed, onScopeDispose, watch, type ComputedRef } from 'vue'

import { marketingInsightsApi } from '../api/MarketingInsightsApi'
import { ExportTrendDataFlow } from '../flows/ExportTrendDataFlow'
import { LoadTrendSeasonalityFlow } from '../flows/LoadTrendSeasonalityFlow'
import {
  TREND_REPORT_ID,
  type TrendSeasonalityReportResponse,
} from '../models/trendSeasonality.types'
import { adaptTrendSeasonalityResponse } from '../service/trendDecomposition'
import { useTrendSeasonalityStore } from '../stores/trendSeasonalityStore'

import { useAccount, useWorkspace } from '@/features/account'
import {
  useReportData,
  useReportSchedules,
  type ReportDataState,
  type ReportFilter,
  type ReportSpec,
} from '@/features/reports'
import { exportXlsx } from '@/shared/lib/export'

export type TrendSeasonalityViewState =
  'needs-keywords' | 'loading' | 'ready' | 'processing' | 'filtered-empty' | 'error'

export interface UseTrendSeasonality {
  store: ReturnType<typeof useTrendSeasonalityStore>
  state: ComputedRef<TrendSeasonalityViewState>
  reportState: ComputedRef<ReportDataState>
  error: ComputedRef<unknown | null>
  reload: () => Promise<void>
  applyFilters: (filters: readonly ReportFilter[]) => void
  exportActive: (labels: {
    filename: string
    sheet: string
    query: string
    month: string
    value: string
    forecast: string
  }) => Promise<void>
}

export function useTrendSeasonality(): UseTrendSeasonality {
  const account = useAccount()
  const workspace = useWorkspace()
  const schedules = useReportSchedules()
  const store = useTrendSeasonalityStore()

  const schedule = computed(() => schedules.getScheduleByReportId(TREND_REPORT_ID))
  const hasKeywordConfiguration = computed(() => Boolean(schedule.value?.additional_info))
  const spec = computed<ReportSpec | null>(() =>
    hasKeywordConfiguration.value
      ? {
          report_id: TREND_REPORT_ID,
          dimensions: ['search_query', 'is_forecasted'],
          breakdown: 'month',
          filters: store.filters,
          pagination_enabled: false,
          partition_name: 'metadata_trend_keyword',
        }
      : null,
  )

  const loadFlow = new LoadTrendSeasonalityFlow({
    api: marketingInsightsApi,
    context: () => {
      const accountId = account.accountId.value
      const workspaceId = workspace.workspaceId.value
      const scheduleId = schedule.value?.id
      return accountId === undefined || workspaceId === undefined || scheduleId === undefined
        ? null
        : { accountId, workspaceId, scheduleId, filters: store.filters }
    },
  })
  const report = useReportData(spec, {
    dataLoader: (context) => loadFlow.execute(context),
  })

  watch(
    report.response,
    (response) => {
      if (!response) {
        store.clearData()
        return
      }
      store.setPayload(adaptTrendSeasonalityResponse(response as TrendSeasonalityReportResponse))
    },
    { immediate: true },
  )

  const state = computed<TrendSeasonalityViewState>(() => {
    if (!hasKeywordConfiguration.value) return 'needs-keywords'
    if (report.state.value === 'loading' || report.state.value === 'idle') return 'loading'
    if (report.state.value === 'error') return 'error'
    if (store.trends.length === 0) return store.filters.length > 0 ? 'filtered-empty' : 'processing'
    if (store.filteredKeywords.length === 0) return 'filtered-empty'
    return 'ready'
  })

  function applyFilters(filters: readonly ReportFilter[]): void {
    store.setFilters(filters)
  }

  const exportFlow = new ExportTrendDataFlow({
    toXlsx: (sheets, filename) =>
      exportXlsx(sheets, filename, {
        telemetry: { fileFormat: 'xlsx', featureName: 'trend-seasonality' },
      }),
  })
  const exportActive: UseTrendSeasonality['exportActive'] = (labels) =>
    exportFlow.execute(store.filteredKeywords, seriesField(store.activeTab), labels)

  onScopeDispose(store.reset)

  return {
    store,
    state,
    reportState: computed(() => report.state.value),
    error: computed(() => report.error.value),
    reload: report.reload,
    applyFilters,
    exportActive,
  }
}

function seriesField(tab: ReturnType<typeof useTrendSeasonalityStore>['activeTab']) {
  return {
    volume: 'observed',
    seasonal: 'seasonal',
    trend: 'trend',
    random: 'residual',
  }[tab] as 'observed' | 'seasonal' | 'trend' | 'residual'
}
